import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { assetUrl } from "@/lib/api";

const INTERVAL = 6000;
const SLIDE_IN = { opacity: 0, scale: 1.04 };
const SLIDE_VISIBLE = { opacity: 1, scale: 1 };
const SLIDE_OUT = { opacity: 0 };
const SLIDE_TRANSITION = { duration: 0.7, ease: [0.22, 1, 0.36, 1] };
const TEXT_IN = { opacity: 0, y: 16 };
const TEXT_VISIBLE = { opacity: 1, y: 0 };
const TEXT_TRANSITION = { duration: 0.5, delay: 0.15, ease: [0.22, 1, 0.36, 1] };
const LABELS = { news: "News", reviews: "Review", videos: "Video", features: "Feature" };

const fmtDate = (d) => {
    try { return new Date(d).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" }); }
    catch { return ""; }
};

export default function HeroCarousel({ posts }) {
    const [idx, setIdx] = useState(0);
    const [paused, setPaused] = useState(false);
    const count = posts.length;

    useEffect(() => {
        if (paused || count < 2) return;
        const t = setTimeout(() => setIdx(i => (i + 1) % count), INTERVAL);
        return () => clearTimeout(t);
    }, [paused, count, idx]);

    if (!count) return null;
    const post = posts[idx];
    const go = (d) => setIdx(i => (i + d + count) % count);

    return (
        <div
            data-testid="hero-carousel"
            className="grid lg:grid-cols-12 gap-6"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="lg:col-span-8 min-w-0 border border-zinc-800 bg-[#0A0A0A] group/main">
                <Link to={`/post/${post.slug}`} className="block group" data-testid="hero-featured">
                    <div className="relative aspect-video overflow-hidden bg-zinc-900">
                        <AnimatePresence initial={false}>
                            <motion.img
                                key={post.id}
                                src={assetUrl(post.cover_image)}
                                alt={post.title}
                                className="absolute inset-0 w-full h-full object-cover"
                                initial={SLIDE_IN}
                                animate={SLIDE_VISIBLE}
                                exit={SLIDE_OUT}
                                transition={SLIDE_TRANSITION}
                            />
                        </AnimatePresence>
                        <span className="absolute top-4 left-4 z-10 font-mono text-[10px] font-bold uppercase tracking-[0.25em] bg-[#FFD000] text-black px-2 py-1">
                            Featured · {String(idx + 1).padStart(2, "0")}/{String(count).padStart(2, "0")}
                        </span>
                    </div>
                    <div className="relative p-6 md:p-8 border-t-2 border-[#FFD000] overflow-hidden">
                        <AnimatePresence mode="wait">
                            <motion.div key={post.id} initial={TEXT_IN} animate={TEXT_VISIBLE} exit={SLIDE_OUT} transition={TEXT_TRANSITION}>
                                <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-3" data-testid="hero-featured-meta">
                                    <span className="text-[#FFD000]">{LABELS[post.category] || post.category}</span>
                                    <span className="mx-2 text-zinc-700">/</span>{fmtDate(post.created_at)}
                                    {post.author_name && <><span className="mx-2 text-zinc-700">/</span>{post.author_name}</>}
                                </div>
                                <div className="flex items-start justify-between gap-6">
                                    <h2 className="font-display text-white text-2xl sm:text-3xl md:text-4xl leading-[1.05] tracking-tight group-hover:text-[#FFD000] transition-colors line-clamp-3" data-testid="hero-featured-title">
                                        {post.title}
                                    </h2>
                                    <ArrowUpRight size={36} weight="bold" className="shrink-0 text-[#FFD000] group-hover:rotate-45 transition-transform duration-300" />
                                </div>
                                {post.excerpt && (
                                    <p className="mt-3 text-sm md:text-base text-zinc-400 leading-relaxed line-clamp-2 max-w-3xl">{post.excerpt}</p>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </Link>
                {count > 1 && <MobileControls count={count} idx={idx} setIdx={setIdx} go={go} />}
            </div>

            {count > 1 && <ThumbRail posts={posts} idx={idx} setIdx={setIdx} paused={paused} />}
        </div>
    );
}

function MobileControls({ count, idx, setIdx, go }) {
    return (
        <div className="lg:hidden flex items-center justify-between px-6 pb-6">
            <div className="flex gap-2">
                {Array.from({ length: count }).map((_, i) => (
                    <button key={i} onClick={() => setIdx(i)} data-testid={`hero-carousel-dot-${i}`}
                            aria-label={`Show featured article ${i + 1}`}
                            className={`h-[3px] transition-[width,background-color] duration-300 ${i === idx ? "w-8 bg-[#FFD000]" : "w-4 bg-zinc-600"}`} />
                ))}
            </div>
            <div className="flex gap-2">
                <button onClick={() => go(-1)} aria-label="Previous" data-testid="hero-carousel-prev"
                        className="w-10 h-10 grid place-items-center border border-zinc-700 text-white hover:border-[#FFD000] hover:text-[#FFD000] transition-colors">
                    <CaretLeft size={16} weight="bold" />
                </button>
                <button onClick={() => go(1)} aria-label="Next" data-testid="hero-carousel-next"
                        className="w-10 h-10 grid place-items-center border border-zinc-700 text-white hover:border-[#FFD000] hover:text-[#FFD000] transition-colors">
                    <CaretRight size={16} weight="bold" />
                </button>
            </div>
        </div>
    );
}

function ThumbRail({ posts, idx, setIdx, paused }) {
    return (
        <div className="hidden lg:flex lg:col-span-4 flex-col border border-zinc-800 bg-[#0A0A0A]" data-testid="hero-thumb-rail">
            <div className="px-5 py-4 border-b border-zinc-800 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">
                // Featured Stories
            </div>
            {posts.map((p, i) => {
                const active = i === idx;
                return (
                    <button key={p.id} onClick={() => setIdx(i)} data-testid={`hero-thumb-${i}`}
                            className={`relative flex-1 flex gap-4 items-center p-5 text-left border-b border-zinc-800 last:border-b-0 transition-colors duration-300 ${active ? "bg-zinc-900" : "hover:bg-zinc-900/60"}`}>
                        <div className="relative w-36 xl:w-40 shrink-0 aspect-video overflow-hidden bg-zinc-900">
                            <img src={assetUrl(p.cover_image)} alt="" loading="lazy"
                                 className={`w-full h-full object-cover transition-[opacity,filter] duration-300 ${active ? "opacity-100" : "opacity-50 grayscale"}`} />
                        </div>
                        <div className="min-w-0">
                            <div className={`font-mono text-[10px] font-bold tracking-[0.25em] mb-1 ${active ? "text-[#FFD000]" : "text-zinc-600"}`}>
                                {String(i + 1).padStart(2, "0")}
                            </div>
                            <div className={`font-display text-base leading-tight line-clamp-3 transition-colors ${active ? "text-white" : "text-zinc-400"}`}>
                                {p.title}
                            </div>
                        </div>
                        <span className="absolute left-0 bottom-0 h-[3px] w-full bg-transparent overflow-hidden">
                            {active && (
                                <motion.span
                                    key={`${p.id}-${paused}`}
                                    className="block h-full bg-[#FFD000] origin-left"
                                    initial={{ scaleX: paused ? 1 : 0 }}
                                    animate={{ scaleX: 1 }}
                                    transition={{ duration: paused ? 0 : INTERVAL / 1000, ease: "linear" }}
                                    data-testid="hero-progress"
                                />
                            )}
                        </span>
                    </button>
                );
            })}
        </div>
    );
}
